import {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import moment from "moment";

import Button from "@mui/material/Button";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	IconButton,
	Checkbox,
	FormControlLabel,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloseIcon from "@mui/icons-material/Close";

import VLFDatePicker from "@/components/Input/DatePicker";
import VLFSelectNew from "@/components/Input/SelectNew";
import VLFTextField from "@/components/Input/TextField";

import {MatchReportAPI} from "@/api/match_report";
import {
	parseMatchReportWorkbook,
	matchPlayerByName,
	readWorkbookFromFile,
} from "@/utils/matchReportImport";
import {
	dateFormat,
	SUCCESSFUL_OPERATION,
	ERROR_OPERATION,
} from "@/utils/constants";

// [fieldKey, label] dei parametri con conteggio POS/NEG
const OFFENSIVE_FIELDS = [
	["shortPass", "Passaggio corto (piede)"],
	["longMidThrow", "Lancio medio e lungo (piede)"],
	["restartWithHands", "Ripartenza con le mani"],
	["remittalOnTheFly", "Ripartenza con rinvio al volo"],
	["positiveTransitionOutside", "Transizione posit. fuori area"],
];
const DEFENSIVE_FIELDS = [
	["saveGrip", "Parata in presa"],
	["saveRebound", "Parata in respinta o deviazione"],
	["highClaim", "Uscita alta presa"],
	["highPunch", "Uscita alta dev. - resp."],
	["lowDive", "Uscita bassa / attacco palla"],
	["depthCoverageInside", "Cop. profondità dentro area"],
	["depthCoverageOutside", "Cop. profondità fuori area"],
];
const CORNER_FIELDS = [
	["cornerOutIntervention", "A uscire · Uscita del portiere"],
	["cornerOutSave", "A uscire · Parata (sviluppo diretto)"],
	["cornerOutDefendedTeammate", "A uscire · Palla difesa dai compagni"],
	["cornerOutOpponents", "A uscire · Palla colpita avversari (no gol)"],
	["cornerOutGoal", "A uscire · Gol (sviluppo diretto)"],
	["cornerRetIntervention", "A rientrare · Uscita del portiere"],
	["cornerRetSave", "A rientrare · Parata (sviluppo diretto)"],
	["cornerRetDefendedTeammate", "A rientrare · Palla difesa dai compagni"],
	["cornerRetOpponents", "A rientrare · Palla colpita avversari (no gol)"],
	["cornerRetGoal", "A rientrare · Gol (sviluppo diretto)"],
	["blockedCorners", "Angoli bloccati (uscire e rientrare)"],
	["shortCorners", "Calci d'angolo battuti corti"],
];
const MARKING_FIELDS = [
	["manToManMarking", "Marcatura a uomo"],
	["mixedMarking", "Marcatura a zona mista"],
	["zonalMarking", "Marcatura a zona"],
];
const GOAL_KEYS = [0, 1, 2, 3, 4].map((i) => "descriptionGoalConceded" + i);

// chiavi numeriche da forzare a numero al salvataggio
const NUMERIC_KEYS = [
	...OFFENSIVE_FIELDS.flatMap(([k]) => [k + "Ok", k + "Failed"]),
	...DEFENSIVE_FIELDS.flatMap(([k]) => [k + "Ok", k + "Failed"]),
	...CORNER_FIELDS.map(([k]) => k),
	...MARKING_FIELDS.map(([k]) => k),
	"minutesPlayed",
	"totalAttacksArea",
];

// Import "Report Gara NUMERICO" portieri da CSV/XLSX (un report per file), con revisione editabile.
const ImportMatchReportModal = (props) => {
	const {open, onClose, players, matches, handleNotification, onImported} =
		props;

	const userName = useSelector((state) => state?.user?.userName);
	const seasonId = useSelector((state) => state?.season?.id);

	const [step, setStep] = useState("upload"); // upload | review | result
	const [loading, setLoading] = useState(false);
	const [fileName, setFileName] = useState("");
	const [parsedMeta, setParsedMeta] = useState(null); // playerName, matchLabel, overallRating
	const [fields, setFields] = useState({}); // valori editabili
	const [warnings, setWarnings] = useState([]);
	const [confidence, setConfidence] = useState("none");
	const [result, setResult] = useState(null);

	// campi da confermare
	const [playerId, setPlayerId] = useState(null);
	const [matchId, setMatchId] = useState(null);
	const [reportDate, setReportDate] = useState(null);
	const [reportAuthor, setReportAuthor] = useState("");

	// controllo report già esistente per (portiere, partita)
	const [duplicate, setDuplicate] = useState(null);
	const [checkingDup, setCheckingDup] = useState(false);
	const [forceImport, setForceImport] = useState(false);

	useEffect(() => {
		let active = true;
		const check = async () => {
			setForceImport(false);
			if (!playerId || !matchId) {
				setDuplicate(null);
				return;
			}
			setCheckingDup(true);
			try {
				const existing = await MatchReportAPI.list({
					playerId,
					matchId,
					seasonId,
				});
				if (active) setDuplicate((existing || []).length ? existing : null);
			} catch (error) {
				if (active) setDuplicate(null);
			} finally {
				if (active) setCheckingDup(false);
			}
		};
		check();
		return () => {
			active = false;
		};
	}, [playerId, matchId, seasonId]);

	const playerOptions = (players || []).map((p) => ({
		value: p.id,
		label: `${p.lastName} ${p.firstName || ""}`.trim(),
	}));
	const matchOptions = (matches || [])
		.map((m) => ({
			value: m.id,
			label: `${moment(m.startDate).format(dateFormat)} ${m.homeClub} - ${m.awayClub}`,
			startDate: m.startDate,
		}))
		.sort((a, b) => new Date(b.startDate) - new Date(a.startDate));

	const setField = (key, value) =>
		setFields((prev) => ({...prev, [key]: value}));

	const resetState = () => {
		setStep("upload");
		setLoading(false);
		setFileName("");
		setParsedMeta(null);
		setFields({});
		setWarnings([]);
		setConfidence("none");
		setResult(null);
		setPlayerId(null);
		setMatchId(null);
		setReportDate(null);
		setReportAuthor("");
		setDuplicate(null);
		setCheckingDup(false);
		setForceImport(false);
	};

	const handleClose = () => {
		resetState();
		onClose();
	};

	const handleFile = async (event) => {
		const file = event.target.files?.[0];
		event.target.value = null;
		if (!file) return;

		setLoading(true);
		setFileName(file.name);
		try {
			const workbook = await readWorkbookFromFile(file);
			const report = parseMatchReportWorkbook(workbook);
			const {player, confidence: conf} = matchPlayerByName(
				report.playerName,
				players,
			);

			setFields(report.fields);
			setParsedMeta({
				playerName: report.playerName,
				matchLabel: report.matchLabel,
				overallRating: report.overallRating,
			});
			setWarnings(report.warnings || []);
			setConfidence(conf);
			setPlayerId(player ? player.id : null);
			setReportDate(
				report.reportDate ? moment(report.reportDate).toDate() : null,
			);
			setReportAuthor(userName || "");
			setStep("review");
		} catch (error) {
			handleNotification(
				"Impossibile leggere il file. Usa un CSV o XLSX valido.",
				"error",
			);
		} finally {
			setLoading(false);
		}
	};

	const canConfirm =
		playerId &&
		matchId &&
		reportDate &&
		reportAuthor &&
		(!duplicate || forceImport);

	const runImport = async () => {
		setLoading(true);
		try {
			const isoDate = moment(reportDate).format("YYYY-MM-DD");
			const body = {...fields};
			NUMERIC_KEYS.forEach((k) => {
				body[k] = Number(body[k]) || 0;
			});
			body.totalGoalsConceded = GOAL_KEYS.filter((k) =>
				String(body[k] || "").trim(),
			).length;
			body.reportType = 7;
			body.reportDate = isoDate;
			body.valuationDate = isoDate;
			body.reportAuthor = reportAuthor;
			body.playerId = playerId;
			body.matchId = matchId;
			body.replaced = !!String(body.reasonReplaced || "").trim();
			body.warned = !!String(body.reasonWarned || "").trim();
			body.expelled = !!String(body.reasonExpelled || "").trim();
			body.reportText = body.generalOpinion || body.notes || "-";

			await MatchReportAPI.create(body);
			setResult({ok: true});
			setStep("result");
			handleNotification(SUCCESSFUL_OPERATION, "success");
			onImported && onImported();
		} catch (error) {
			setResult({ok: false});
			setStep("result");
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const confidenceBadge = () => {
		const map = {
			exact: {label: "match certo", color: "#2e7d32", bg: "rgba(46,125,50,0.12)"},
			probable: {
				label: "match probabile",
				color: "#b26a00",
				bg: "rgba(178,106,0,0.12)",
			},
			none: {
				label: "da abbinare",
				color: "#c62828",
				bg: "rgba(198,40,40,0.12)",
			},
		};
		const c = map[confidence] || map.none;
		return (
			<span
				style={{
					color: c.color,
					backgroundColor: c.bg,
					borderRadius: 8,
					padding: "2px 8px",
					fontSize: "0.75rem",
					fontWeight: 600,
					marginLeft: 8,
				}}
			>
				{c.label}
			</span>
		);
	};

	// helper input
	const numInput = (key, label) => (
		<VLFTextField
			label={label}
			type="number"
			size="small"
			value={fields[key] ?? ""}
			onChange={(e) => setField(key, e.target.value)}
		/>
	);
	const txtInput = (key, label, multiline = false) => (
		<VLFTextField
			label={label}
			size="small"
			value={fields[key] ?? ""}
			onChange={(e) => setField(key, e.target.value)}
			multiline={multiline}
			minRows={multiline ? 3 : undefined}
			maxRows={multiline ? 6 : undefined}
		/>
	);

	const Section = ({title, children}) => (
		<div
			style={{border: "1px solid #ebf1fd", borderRadius: 12, padding: 12}}
			className="mt-2"
		>
			<div style={{fontWeight: 700, marginBottom: 8}}>{title}</div>
			{children}
		</div>
	);

	// riga parametro con POS/NEG
	const PosNegRow = ([key, label]) => (
		<div key={key} className="row no-margin align-items-center mb-1">
			<div className="col-6" style={{fontSize: "0.85rem"}}>
				{label}
			</div>
			<div className="col-3">{numInput(key + "Ok", "POS")}</div>
			<div className="col-3">{numInput(key + "Failed", "NEG")}</div>
		</div>
	);

	const renderReview = () => (
		<div className="d-flex flex-column">
			{warnings.length ? (
				<div
					style={{
						backgroundColor: "rgba(178,106,0,0.10)",
						borderRadius: 10,
						padding: "8px 12px",
						fontSize: "0.85rem",
					}}
				>
					{warnings.map((w, i) => (
						<div key={i}>⚠️ {w}</div>
					))}
				</div>
			) : null}

			{checkingDup ? (
				<div
					style={{fontSize: "0.8rem", color: "#6b7a99"}}
					className="mt-2"
				>
					Verifica report esistenti…
				</div>
			) : null}
			{duplicate ? (
				<div
					className="mt-2"
					style={{
						backgroundColor: "rgba(198,40,40,0.10)",
						border: "1px solid rgba(198,40,40,0.30)",
						borderRadius: 10,
						padding: "8px 12px",
						fontSize: "0.85rem",
					}}
				>
					⚠️ Esiste già un report gara per questo portiere in questa partita
					{duplicate[0]?.reportDate
						? ` (del ${moment(duplicate[0].reportDate).format(dateFormat)})`
						: ""}
					.
					<div>
						<FormControlLabel
							control={
								<Checkbox
									size="small"
									checked={forceImport}
									onChange={(e) => setForceImport(e.target.checked)}
								/>
							}
							label="Importa comunque (creerà un duplicato)"
						/>
					</div>
				</div>
			) : null}

			<Section title="Report">
				<div className="row no-margin">
					<div className="col-12 col-md-6 padding-small">
						<div className="field-label-container">
							<div className="field-label">
								Giocatore (file: {parsedMeta?.playerName || "—"})
								{confidenceBadge()}
							</div>
						</div>
						<VLFSelectNew
							options={playerOptions}
							value={playerOptions.find((o) => o.value === playerId) || null}
							onChange={(v) => setPlayerId(v ? v.value : null)}
							placeholder="Seleziona portiere…"
							required
						/>
					</div>
					<div className="col-12 col-md-6 padding-small">
						<div className="field-label-container">
							<div className="field-label">
								Partita (file: {parsedMeta?.matchLabel || "—"})
							</div>
						</div>
						<VLFSelectNew
							options={matchOptions}
							value={matchOptions.find((o) => o.value === matchId) || null}
							onChange={(v) => setMatchId(v ? v.value : null)}
							sorted={false}
							placeholder="Seleziona partita…"
							required
						/>
					</div>
					<div className="col-12 col-md-6 padding-small">
						<VLFDatePicker
							label="Data report"
							value={reportDate}
							onChange={setReportDate}
							format="DD-MM-YYYY"
							size="small"
							required
						/>
					</div>
					<div className="col-12 col-md-6 padding-small">
						<VLFTextField
							label="Allenatore"
							value={reportAuthor}
							onChange={(e) => setReportAuthor(e.target.value)}
							size="small"
						/>
					</div>
					<div className="col-6 col-md-3 padding-small">
						{numInput("minutesPlayed", "Min. giocati")}
					</div>
					<div className="col-6 col-md-3 padding-small">
						{numInput("totalAttacksArea", "Attacchi area")}
					</div>
					<div className="col-12 col-md-6 padding-small">
						{txtInput("heatingExecution", "Esecuzione riscaldamento")}
					</div>
				</div>
			</Section>

			<Section title="Disciplinare & note">
				<div className="row no-margin">
					<div className="col-12 col-md-4 padding-small">
						{txtInput("reasonReplaced", "Sostituito (motivo)")}
					</div>
					<div className="col-12 col-md-4 padding-small">
						{txtInput("reasonWarned", "Ammonito (motivo)")}
					</div>
					<div className="col-12 col-md-4 padding-small">
						{txtInput("reasonExpelled", "Espulso (motivo)")}
					</div>
					<div className="col-12 padding-small">
						{txtInput("notes", "Note", true)}
					</div>
					<div className="col-12 padding-small">
						{txtInput("generalOpinion", "Opinione generale sulla gara", true)}
					</div>
				</div>
			</Section>

			<Section title="Goal subiti">
				<div className="row no-margin">
					{GOAL_KEYS.map((k, i) => (
						<div key={k} className="col-12 padding-small">
							{txtInput(k, `Goal ${i + 1}`)}
						</div>
					))}
				</div>
			</Section>

			<Section title="Gioco offensivo (POS / NEG)">
				{OFFENSIVE_FIELDS.map(PosNegRow)}
			</Section>

			<Section title="Gioco difensivo (POS / NEG)">
				{DEFENSIVE_FIELDS.map(PosNegRow)}
			</Section>

			<Section title="Strategia difensiva angoli">
				<div className="row no-margin">
					{CORNER_FIELDS.map(([k, label]) => (
						<div key={k} className="col-12 col-md-6 padding-small">
							{numInput(k, label)}
						</div>
					))}
				</div>
				<div className="d-flex flex-wrap gap-3 mt-2">
					{MARKING_FIELDS.map(([k, label]) => (
						<FormControlLabel
							key={k}
							control={
								<Checkbox
									size="small"
									checked={!!Number(fields[k])}
									onChange={(e) => setField(k, e.target.checked ? 1 : 0)}
								/>
							}
							label={label}
						/>
					))}
				</div>
			</Section>

			{parsedMeta?.overallRating ? (
				<div className="mt-2" style={{fontSize: "0.8rem", color: "#6b7a99"}}>
					Voto generale nel file: {parsedMeta.overallRating} (non importato — campo
					assente nel modello)
				</div>
			) : null}
		</div>
	);

	const renderUpload = () => (
		<div className="d-flex flex-column align-items-center gap-3 py-3">
			<div className="text-center" style={{maxWidth: 460}}>
				Carica il Report Gara NUMERICO portieri (esportato da Numbers in{" "}
				<b>CSV</b> o <b>XLSX</b>). Potrai rivedere e correggere i valori prima di
				importare.
			</div>
			<Button variant="contained" component="label" startIcon={<UploadFileIcon />}>
				Scegli file
				<input
					type="file"
					hidden
					accept=".csv,.xlsx,.xls"
					onChange={handleFile}
				/>
			</Button>
			{fileName ? <div className="text-muted">{fileName}</div> : null}
		</div>
	);

	const renderResult = () => (
		<div className="d-flex flex-column align-items-center gap-2 py-4">
			<div style={{fontSize: "1.1rem", fontWeight: 700}}>
				{result?.ok ? "✅ Report importato" : "❌ Import non riuscito"}
			</div>
			<div style={{color: "#6b7a99"}}>
				{result?.ok
					? "Il report gara è stato creato."
					: "Si è verificato un errore durante la creazione."}
			</div>
		</div>
	);

	const content =
		step === "upload"
			? renderUpload()
			: step === "review"
				? renderReview()
				: renderResult();

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="md"
			fullWidth
			PaperProps={{className: "vlf-modal__paper"}}
		>
			<DialogTitle
				className="vlf-modal__title"
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
				}}
			>
				<span className="vlf-modal__title-text">
					Importa report gara (portieri)
				</span>
				<IconButton size="small" onClick={handleClose} aria-label="chiudi">
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>
			<DialogContent className="vlf-modal__content dialog-container">
				{content}
			</DialogContent>
			<DialogActions className="vlf-modal__actions">
				{step === "review" ? (
					<>
						<Button
							color="error"
							variant="outlined"
							onClick={() => setStep("upload")}
							disabled={loading}
						>
							Indietro
						</Button>
						<Button
							variant="contained"
							disabled={!canConfirm || loading}
							onClick={runImport}
						>
							Importa
						</Button>
					</>
				) : step === "result" ? (
					<Button variant="contained" onClick={handleClose}>
						Chiudi
					</Button>
				) : (
					<Button color="error" variant="outlined" onClick={handleClose}>
						Annulla
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
};

export default ImportMatchReportModal;
